// Work queue — spec ch.06: "Status filters, search, source review and batch
// review … Server-filtered proposals, role/admin grants, expected revisions and
// one operation per approved proposal. Batch review requires explicit
// selections and individual results; no blanket approval of hidden items."
//
// Today shows the top of this queue; this screen is the whole of it, with the
// filters and the batch review that sentence asks for. Five rules, and four of
// them are about the batch.
//
// ---------------------------------------------------------------------------
// 1. Every filter is the server's
// ---------------------------------------------------------------------------
//
// The kind, the state and the administration all travel to `GET /work`, and the
// counts come back from the same transaction as the items. Filtering a
// cursor-paginated list in the browser would narrow the PAGE rather than the
// queue — the objection Activity.tsx already records — and here it would be
// worse, because the thing being narrowed is what a reviewer is about to
// approve.
//
// The tab badges are the endpoint's `counts`, which stay the denominators: they
// say how much open work there IS per kind, and the list says what was asked
// for. So "3 shown of 12 open documents" is two numbers with two meanings
// rather than a count that disagrees with its list.
//
// ---------------------------------------------------------------------------
// 2. Batch review is a batch of individual reviews
// ---------------------------------------------------------------------------
//
// "Explicit selections and individual results; no blanket approval of hidden
// items" is not a warning about a checkbox. It is the shape of the whole
// interaction:
//
//   * Nothing is selected by default and there is no "select all": a selection
//     is a row a person ticked. Changing a filter CLEARS the selection, because
//     a selected row that is no longer on screen is exactly a hidden item.
//   * Selecting does not approve. The next step READS each selected document —
//     one `GET /documents/{id}` each — and shows what would be approved: the
//     invoice, the amounts, the revision, and for anything that cannot be
//     approved the reason. A reviewer sees the list before the writes, not
//     after.
//   * Approving sends one request per item, each with its own idempotency key
//     and its own expected revision, and each row reports its own outcome. A
//     batch that returned one verdict for twelve invoices would be the blanket
//     approval the spec forbids, dressed as a success message.
//   * The expected revision and the payload hash come from the read taken
//     moments earlier, so a document that moved in between answers 409 and that
//     row says so while the others proceed. That is the same rule the document
//     workbench keeps, applied per item.
//
// ---------------------------------------------------------------------------
// 3. Only documents can be batch-reviewed, and the reason is an absent endpoint
// ---------------------------------------------------------------------------
//
// Approving needs the payload hash and the source version of the revision being
// approved, and the only endpoint that reports them is `GET /documents/{id}`. A
// proposal work item carries the proposal's id, and there is no
// `GET /proposals/{id}` to resolve it — so the proposals tab lists them and
// offers no checkbox, and says why. Inventing a batch that approved proposals
// by guessing their revision is precisely what the expected-revision check
// exists to stop.
//
// ---------------------------------------------------------------------------
// 4. Queued is the strongest thing said here
// ---------------------------------------------------------------------------
//
// Each approved item reports `202 {operationId, status:'queued'}` and the badge
// says Queued. This screen does not poll: twelve concurrent polls would be
// twelve claims to keep straight, and the place where an outcome is reported is
// the document — one implementation of "only a receipt confirms an effect",
// which is the rule states.ts enforces over the whole table.

//
// ---------------------------------------------------------------------------
// 5. One row per document
// ---------------------------------------------------------------------------
//
// A document and its own booking proposal are one item: approving the proposal
// is how the document is finished. GET /work folds the proposal into the
// document (its summary carries `proposalStatus`), so the list and the counts
// both have one row per document, and the row's status says which of the two
// things a reviewer has to do: approve it, or check it first.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, newIdempotencyKey, request, tenantPath } from '../api/client.ts';
import type { Accepted, DocumentDetail, Money, WorkItem, WorkQueue } from '../api/types.ts';
import { EmptyState } from '../components/EmptyState.tsx';
import { documentOf } from '../components/WorkItemLine.tsx';
import type { ScreenId } from '../components/Shell.tsx';
import {
  DOCUMENT_STATES,
  MATCH_STATES,
  OPERATION_STATES,
  PROPOSAL_STATES,
  REQUEST_STATES,
  useStates,
  type LocalStates,
  type StateDisplay,
  type Tone,
} from '../states.ts';
import { administrationFilter, useWorkspace } from '../state/workspace.tsx';
import { en } from '../i18n/en.ts';
import { translate, useLocale, useT, type MessageKey, type Translate } from '../i18n/index.tsx';
import { shortDate } from '../i18n/format.ts';
import * as format from '../format.ts';

type Kind = WorkItem['kind'];

/**
 * A tab is a kind, or every kind at once. "All" asks /work with no `kind`, so
 * its list is the server's; its badge is the sum of the server's per-kind
 * counts, which is the same scope and the same snapshot, not a count of a page.
 *
 * Proposals and operations get a tab only when there are any: a proposal is its
 * own item only once its document has left the queue, and an operation only
 * while a booking is under way or stuck.
 */
type Tab = Kind | 'all';
const FIXED_TABS: readonly Tab[] = ['all', 'document', 'bank_match', 'request'];
const OPTIONAL_TABS: readonly Kind[] = ['proposal', 'operation'];
const KINDS: readonly Kind[] = ['document', 'proposal', 'operation', 'bank_match', 'request'];

/** Which state table describes a work item of this kind. */
function tableFor(kind: Kind): Readonly<Record<string, StateDisplay>> {
  switch (kind) {
    case 'document':
      return DOCUMENT_STATES;
    case 'proposal':
      return PROPOSAL_STATES;
    case 'operation':
      return OPERATION_STATES;
    case 'bank_match':
      return MATCH_STATES;
    default:
      return REQUEST_STATES;
  }
}

/**
 * The states each kind can actually be in WHILE IN THE QUEUE.
 *
 * Not every state in states.ts: the queue defines "open" by exclusion, so a
 * document that has been approved or excluded is not in it, and offering
 * "Approved" as a filter would be offering an option that always answers with
 * nothing. The authority is the WORK_ITEMS CTE in packages/db/read-models.ts,
 * and tests/api/endpoints.test.ts pins these lists against the database's own
 * enums.
 */
const QUEUE_STATES: Readonly<Record<Kind, readonly string[]>> = {
  document: ['received', 'quarantined', 'extracting', 'needs_review', 'ready'],
  proposal: ['draft', 'needs_review', 'ready', 'stale'],
  operation: ['queued', 'submitting', 'failed_safe', 'unknown', 'needs_review'],
  bank_match: ['candidate', 'stale'],
  request: [],
};

/** Roles the API lets approve. Anything else is told why, not shown a dead button. */
const MAY_APPROVE = new Set(['owner', 'reviewer']);

/**
 * The one status a row shows. "Klaar voor goedkeuring" when the document's
 * proposal is ready, "Controleren" whenever a person has to look first, and
 * otherwise the state's own (localised) label.
 */
export function rowStatus(item: WorkItem, states: LocalStates, t: Translate): { label: string; tone: Tone } {
  const ready = { label: t('queue.status.ready'), tone: 'ai' as Tone };
  const check = { label: t('queue.status.check'), tone: 'warn' as Tone };
  if (item.kind === 'document') {
    if (item.state === 'needs_review' || item.state === 'quarantined') return check;
    if (item.state === 'ready') {
      const proposal = item.summary?.proposalStatus;
      return proposal === undefined || proposal === null || proposal === 'ready' ? ready : check;
    }
  }
  if (item.kind === 'proposal') return item.state === 'ready' ? ready : check;
  const shown = states.display(tableFor(item.kind), item.state);
  return { label: shown.label, tone: shown.tone };
}

/** A document whose proposal is ready: the only rows a batch review can approve. */
function selectable(item: WorkItem): boolean {
  if (item.kind !== 'document' || item.state !== 'ready') return false;
  const proposal = item.summary?.proposalStatus;
  return proposal === undefined || proposal === null || proposal === 'ready';
}

function kindLabel(item: WorkItem, t: Translate): string {
  if (item.kind === 'document') {
    const key = `work.docType.${item.summary?.documentType ?? item.detail ?? ''}`;
    return key in en ? t(key as MessageKey) : t('work.kind.document');
  }
  return t(`work.kind.${item.kind}` as MessageKey);
}

type Load =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | {
      readonly kind: 'ready';
      readonly counts: WorkQueue['counts'];
      readonly items: readonly WorkItem[];
      readonly nextCursor: string | null;
      readonly loadingMore: boolean;
    };

/** What one selected document turned out to be, once it was read. */
interface Reviewed {
  readonly documentId: string;
  readonly detail: string | null;
  readonly document: DocumentDetail | null;
  /** Null when this item can be approved; otherwise why it cannot. */
  readonly blocked: string | null;
  readonly result: Result | null;
}

type Result =
  | { readonly kind: 'queued'; readonly operationId: string }
  | { readonly kind: 'refused'; readonly message: string };

type Batch =
  | { readonly phase: 'reading'; readonly ids: readonly string[] }
  | { readonly phase: 'reviewing'; readonly entries: readonly Reviewed[] }
  | { readonly phase: 'approving'; readonly entries: readonly Reviewed[] }
  | { readonly phase: 'done'; readonly entries: readonly Reviewed[] };

export interface WorkQueueScreenProps {
  readonly onOpenDocument: (documentId: string) => void;
  /** Where a bank match or a customer request is opened. */
  readonly onNavigate?: (screen: ScreenId) => void;
  /** The tab it opens on: everything, as the design does, unless told otherwise. */
  readonly initialKind?: Tab;
}

export function WorkQueueScreen({ onOpenDocument, onNavigate, initialKind = 'all' }: WorkQueueScreenProps): ReactNode {
  const workspace = useWorkspace();
  const t = useT();
  const states = useStates();
  const { locale } = useLocale();
  const { token, administrationId } = workspace;
  const tenantId = workspace.membership.tenantId;
  const role = workspace.membership.role;

  const [kind, setKind] = useState<Tab>(initialKind);
  const [state, setState] = useState<string>('');
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [batch, setBatch] = useState<Batch | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    const filter = administrationFilter(administrationId);
    if (filter !== undefined) params.set('administrationId', filter);
    if (kind !== 'all') params.set('kind', kind);
    if (state !== '') params.set('state', state);
    return params;
  }, [administrationId, kind, state]);

  const fetchPage = useCallback(
    async (cursor: string | null): Promise<WorkQueue> => {
      const params = new URLSearchParams(query);
      if (cursor !== null) params.set('cursor', cursor);
      return request<WorkQueue>(tenantPath(tenantId, `/work?${params.toString()}`), { token });
    },
    [tenantId, token, query],
  );

  useEffect(() => {
    let cancelled = false;
    // Reset first, and clear the selection with it. A selected row that the new
    // filter does not show is a hidden item, and approving it later would be
    // exactly what ch.06 forbids — so changing what is on screen throws the
    // selection away rather than carrying it invisibly.
    setLoad({ kind: 'loading' });
    setSelected([]);
    setBatch(null);
    void (async () => {
      try {
        const page = await fetchPage(null);
        if (cancelled) return;
        setLoad({ kind: 'ready', counts: page.counts, items: page.items, nextCursor: page.nextCursor, loadingMore: false });
      } catch (caught) {
        if (cancelled) return;
        setLoad({
          kind: 'error',
          message: caught instanceof ApiError && caught.isAbsent ? t('queue.error.absent') : t('queue.error.read'),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // Keyed on what is read: a language switch must not re-read.
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (load.kind !== 'ready' || load.nextCursor === null || load.loadingMore) return;
    const cursor = load.nextCursor;
    setLoad({ ...load, loadingMore: true });
    try {
      const page = await fetchPage(cursor);
      setLoad((current) =>
        current.kind === 'ready'
          ? {
              kind: 'ready',
              // The endpoint's sort is total — (ordered_at, id) — so a page
              // cannot repeat a row, and appending is safe.
              counts: page.counts,
              items: [...current.items, ...page.items],
              nextCursor: page.nextCursor,
              loadingMore: false,
            }
          : current,
      );
    } catch {
      setLoad((current) => (current.kind === 'ready' ? { ...current, loadingMore: false } : current));
    }
  }, [load, fetchPage]);

  const toggle = useCallback((documentId: string) => {
    setSelected((current) =>
      current.includes(documentId) ? current.filter((id) => id !== documentId) : [...current, documentId],
    );
  }, []);

  /** Read every selected document, and work out which of them can be approved. */
  const review = useCallback(async () => {
    setBatch({ phase: 'reading', ids: selected });
    const entries: Reviewed[] = [];
    for (const documentId of selected) {
      const found = load.kind === 'ready' ? load.items.find((item) => item.id === documentId) : undefined;
      const detail = found === undefined ? null : (found.summary?.supplierName ?? kindLabel(found, t));
      try {
        const document = await request<DocumentDetail>(
          tenantPath(tenantId, `/documents/${encodeURIComponent(documentId)}`),
          { token },
        );
        entries.push({ documentId, detail, document, blocked: approvalBlocked(document, role, t), result: null });
      } catch (caught) {
        entries.push({
          documentId,
          detail,
          document: null,
          blocked: caught instanceof ApiError && caught.isAbsent ? t('queue.blocked.absent') : t('queue.blocked.unreadable'),
          result: null,
        });
      }
    }
    setBatch({ phase: 'reviewing', entries });
  }, [selected, tenantId, token, role, load, t]);

  /**
   * Approve the entries that can be approved, one request each.
   *
   * Sequential rather than parallel, and that is a decision about failure
   * rather than about load: each item's outcome is recorded before the next is
   * sent, so an interruption halfway leaves a screen that says which ones were
   * queued instead of a screen that says nothing about twelve of them.
   */
  const approve = useCallback(async () => {
    if (batch === null || (batch.phase !== 'reviewing' && batch.phase !== 'done')) return;
    const entries = [...batch.entries];
    setBatch({ phase: 'approving', entries });

    for (let index = 0; index < entries.length; index += 1) {
      const entry = entries[index] as Reviewed;
      if (entry.blocked !== null || entry.result !== null || entry.document?.proposal == null) continue;
      const proposal = entry.document.proposal;
      if (proposal.payloadHash === null || proposal.sourceVersionId === null) continue;

      // One key per item per intention. Two items never share one: a shared key
      // would make the second request a replay of the first and return the
      // first's operation id for a different invoice.
      const idempotencyKey = newIdempotencyKey();
      let result: Result;
      try {
        const accepted = await request<Accepted>(
          tenantPath(tenantId, `/proposals/${encodeURIComponent(proposal.id)}/approve`),
          {
            method: 'POST',
            token,
            idempotencyKey,
            body: {
              // The revision and the hash from the read taken moments ago, so a
              // document that moved in between is refused rather than approved
              // against something nobody saw.
              expectedRevision: proposal.currentRevision,
              proposalHash: proposal.payloadHash,
              sourceVersionId: proposal.sourceVersionId,
              decision: 'approve_submission',
            },
          },
        );
        result = { kind: 'queued', operationId: accepted.operationId };
      } catch (caught) {
        result = { kind: 'refused', message: refusal(caught, t) };
      }
      entries[index] = { ...entry, result };
      setBatch({ phase: 'approving', entries: [...entries] });
    }

    setBatch({ phase: 'done', entries });
    // The queue has moved: an approved document leaves it. Re-read rather than
    // removing rows here, so what is on screen is what the server says.
    try {
      const page = await fetchPage(null);
      setLoad({ kind: 'ready', counts: page.counts, items: page.items, nextCursor: page.nextCursor, loadingMore: false });
      setSelected([]);
    } catch {
      // Leave the list as it was; the results above are still the truth about
      // what was sent.
    }
  }, [batch, tenantId, token, fetchPage, t]);

  const counts = load.kind === 'ready' ? load.counts : null;
  const items = load.kind === 'ready' ? load.items : [];
  const tabCount = (tab: Tab): number | null =>
    counts === null ? null : tab === 'all' ? KINDS.reduce((total, entry) => total + counts[entry], 0) : counts[tab];
  const tabs: readonly Tab[] = [
    ...FIXED_TABS,
    ...OPTIONAL_TABS.filter((entry) => entry === kind || (counts !== null && counts[entry] > 0)),
  ];
  const stateOptions = kind === 'all' ? [] : QUEUE_STATES[kind];

  const open = (item: WorkItem) => {
    const documentId = documentOf(item);
    if (documentId !== null) onOpenDocument(documentId);
    else if (item.kind === 'bank_match') onNavigate?.('bank');
    else if (item.kind === 'request') onNavigate?.('messages');
  };

  return (
    <div className="queue" data-testid="work-queue">
      <header className="page-head">
        <h1>{t('nav.work')}</h1>
        <p>{t('queue.intro')}</p>
      </header>

      {load.kind === 'error' ? (
        <p className="error-banner" role="alert">
          {load.message}
        </p>
      ) : null}

      <section className="dash-card list-card queue-card">
        <div className="queue-bar">
          <div className="tabs" role="tablist" aria-label={t('queue.tabs')}>
            {tabs.map((entry) => (
              <button
                key={entry}
                type="button"
                role="tab"
                aria-selected={entry === kind}
                className={entry === kind ? 'on' : ''}
                onClick={() => {
                  setKind(entry);
                  // A state from the previous kind means nothing here: `ready`
                  // is a proposal state and `needs_review` a document one.
                  setState('');
                }}
              >
                {t(`queue.tab.${entry}` as MessageKey)}
                {counts === null ? null : <span className="count">{tabCount(entry)}</span>}
              </button>
            ))}
          </div>
          {stateOptions.length === 0 ? null : (
            <select
              className="head-select"
              aria-label={t('queue.filter')}
              value={state}
              onChange={(event) => setState(event.target.value)}
            >
              <option value="">{t('queue.filter.all')}</option>
              {stateOptions.map((value) => (
                <option key={value} value={value}>
                  {kind === 'all' ? value : states.display(tableFor(kind), value).label}
                </option>
              ))}
            </select>
          )}
        </div>

        {load.kind === 'loading' ? (
          <p className="dash-loading">{t('queue.reading')}</p>
        ) : items.length === 0 ? (
          <EmptyState icon="done" testId="queue-empty" text={state === '' ? t('queue.empty') : t('queue.emptyFiltered')} />
        ) : (
          <ul className="queue-rows">
            {items.map((item) => {
              const status = rowStatus(item, states, t);
              const invoice = item.summary?.invoiceNumber ?? null;
              const supplier = item.summary?.supplierName ?? null;
              const name = supplier ?? kindLabel(item, t);
              // The kind is the sub line's first word, unless it is already the name.
              const sub =
                item.kind === 'bank_match' && invoice !== null
                  ? t('today.waiting.paymentFound', { invoice })
                  : [supplier === null ? null : kindLabel(item, t), invoice].filter((part): part is string => part !== null).join(' ');
              const canSelect = selectable(item);
              const openable = documentOf(item) !== null || item.kind === 'bank_match' || item.kind === 'request';
              return (
                <li className="queue-row" key={`${item.kind}:${item.id}`} data-testid={`queue-${item.kind}-${item.id}`}>
                  <span className="queue-check">
                    {canSelect ? (
                      <input
                        type="checkbox"
                        checked={selected.includes(item.id)}
                        onChange={() => toggle(item.id)}
                        aria-label={t('queue.select', { name: invoice === null ? name : `${name} ${invoice}` })}
                      />
                    ) : null}
                  </span>
                  <div className="queue-name">
                    <strong>{name}</strong>
                    <p>{sub}</p>
                  </div>
                  <span className="queue-date" title={format.dateTime(item.orderedAt)}>
                    {shortDate(item.orderedAt.slice(0, 10), locale)}
                  </span>
                  <span className="queue-status">
                    <span className={`badge ${status.tone === 'neutral' ? '' : status.tone}`} data-testid="queue-status">
                      {status.label}
                    </span>
                  </span>
                  <span className="queue-amt">{item.summary?.amount == null ? '' : format.money(item.summary.amount)}</span>
                  <span className="queue-action">
                    {openable ? (
                      <button type="button" className="btn row-btn" onClick={() => open(item)}>
                        {t('common.view')}
                      </button>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {selected.length === 0 && (load.kind !== 'ready' || load.nextCursor === null) ? null : (
          <div className="queue-foot">
            {load.kind === 'ready' && load.nextCursor !== null ? (
              <button type="button" className="btn" disabled={load.loadingMore} onClick={() => void loadMore()}>
                {load.loadingMore ? t('queue.loadingMore') : t('queue.loadMore')}
              </button>
            ) : (
              <span />
            )}
            {selected.length === 0 ? null : (
              <div className="flex">
                <button
                  type="button"
                  className="btn primary"
                  data-testid="selected-count"
                  disabled={batch?.phase === 'reading'}
                  onClick={() => void review()}
                >
                  {batch?.phase === 'reading' ? t('queue.reviewReading') : t('queue.review', { count: selected.length })}
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {batch === null || batch.phase === 'reading' ? null : (
        <BatchReview
          batch={batch}
          role={role}
          onApprove={() => void approve()}
          onOpenDocument={onOpenDocument}
          onClose={() => setBatch(null)}
        />
      )}
    </div>
  );
}

/**
 * What the batch will do, before it does it — and what it did, after.
 *
 * One table, one row per selected document, one outcome per row. ch.06's
 * "individual results" is this table after the writes; "no blanket approval of
 * hidden items" is this table before them.
 */
function BatchReview({
  batch,
  role,
  onApprove,
  onOpenDocument,
  onClose,
}: {
  readonly batch: Batch;
  readonly role: string;
  readonly onApprove: () => void;
  readonly onOpenDocument: (documentId: string) => void;
  readonly onClose: () => void;
}): ReactNode {
  const t = useT();
  const entries = batch.phase === 'reading' ? [] : batch.entries;
  const ready = entries.filter((entry) => entry.blocked === null && entry.result === null);
  const done = batch.phase === 'done';
  const queued = entries.filter((entry) => entry.result?.kind === 'queued');
  const refused = entries.filter((entry) => entry.result?.kind === 'refused');

  return (
    <section className="dash-card list-card" data-testid="batch-review">
      <div className="dash-head">
        <div>
          <h2>{done ? t('queue.batch.titleDone') : t('queue.batch.title')}</h2>
          <p className="dash-sub">
            {done
              ? t('queue.batch.doneLine', {
                  queued: queued.length,
                  refused: refused.length,
                  other: entries.length - queued.length - refused.length,
                })
              : t('queue.batch.line', { selected: entries.length, ready: ready.length })}
          </p>
        </div>
        <button type="button" className="btn" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{t('queue.batch.document')}</th>
              <th>{t('queue.batch.invoice')}</th>
              <th>{t('queue.batch.total')}</th>
              <th>{t('queue.batch.revision')}</th>
              <th>{t('queue.batch.outcome')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.documentId} data-testid={`batch-${entry.documentId}`}>
                <td>
                  <b>{entry.detail ?? t('queue.batch.document')}</b>
                  <small>{entry.documentId.slice(0, 8)}…</small>
                </td>
                <td>{entry.document?.proposal?.invoiceNumber ?? '—'}</td>
                {/* Decimal strings, printed as they arrived. ch.02 forbids
                    floating-point arithmetic for financial decisions, and
                    parsing one to format it is that, one layer out. */}
                <td>{amount(entry.document?.proposal?.total ?? null)}</td>
                <td>
                  {entry.document?.proposal === null || entry.document?.proposal === undefined
                    ? '—'
                    : entry.document.proposal.currentRevision}
                </td>
                <td>
                  <Outcome entry={entry} phase={batch.phase} />
                </td>
                <td>
                  <button type="button" className="table-action" onClick={() => onOpenDocument(entry.documentId)}>
                    {t('queue.batch.open')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card-foot between">
        <span>
          {done
            ? /* Rule 4. Queued is the strongest thing this screen says, and the
                 outcome lives where it is reported once. */
              t('queue.batch.footDone')
            : MAY_APPROVE.has(role)
              ? t('queue.batch.foot')
              : t('queue.blocked.role', { role: role.replace('_', ' ') })}
        </span>
        {done ? null : (
          <button
            type="button"
            className="btn primary"
            disabled={ready.length === 0 || batch.phase === 'approving' || !MAY_APPROVE.has(role)}
            onClick={onApprove}
          >
            {batch.phase === 'approving' ? t('queue.batch.approving') : t('queue.batch.approve', { count: ready.length })}
          </button>
        )}
      </div>
    </section>
  );
}

function Outcome({ entry, phase }: { readonly entry: Reviewed; readonly phase: Batch['phase'] }): ReactNode {
  const t = useT();
  if (entry.result !== null) {
    return entry.result.kind === 'queued' ? (
      <>
        <span className="badge blue">{t('queue.outcome.queued')}</span>
        <small>{entry.result.operationId.slice(0, 8)}…</small>
      </>
    ) : (
      <>
        <span className="badge danger">{t('queue.outcome.notSent')}</span>
        <small>{entry.result.message}</small>
      </>
    );
  }
  if (entry.blocked !== null) {
    return (
      <>
        <span className="badge warn">{t('queue.outcome.blocked')}</span>
        <small>{entry.blocked}</small>
      </>
    );
  }
  return phase === 'approving' ? (
    <span className="muted">{t('queue.outcome.waiting')}</span>
  ) : (
    <span className="badge ok">{t('queue.outcome.ready')}</span>
  );
}

const english: Translate = (key, vars) => translate('en', key, vars);

/**
 * Why this document cannot be approved, or null when it can.
 *
 * Every one of these is a refusal the server would make anyway — the role, the
 * proposal's state, a blocker, a revision with no hash. Saying it here means a
 * reviewer sees it in the list before the writes rather than as a row that
 * failed. English unless given a `t`.
 */
export function approvalBlocked(document: DocumentDetail, role: string, t: Translate = english): string | null {
  if (!MAY_APPROVE.has(role)) return t('queue.blocked.role', { role: role.replace('_', ' ') });
  const proposal = document.proposal;
  if (proposal === null) return t('queue.blocked.noProposal');
  if (document.blockers.length > 0) {
    return t('queue.blocked.blockers', { codes: document.blockers.map((blocker) => blocker.code).join(', ') });
  }
  if (proposal.status !== 'ready') {
    return t('queue.blocked.status', {
      status: t(`state.proposal.${proposal.status}.label` as MessageKey).toLowerCase(),
    });
  }
  if (proposal.payloadHash === null || proposal.sourceVersionId === null) return t('queue.blocked.noRevision');
  return null;
}

function amount(value: Money | null): string {
  return value === null ? '—' : `${value.currency} ${value.amount}`;
}

/** One item's refusal, in words, without claiming anything about the others. */
function refusal(caught: unknown, t: Translate): string {
  if (!(caught instanceof ApiError)) return t('queue.refusal.generic');
  if (caught.isStale) return t('queue.refusal.stale');
  if (caught.status === 403) return t('queue.refusal.forbidden');
  if (caught.isAbsent) return t('queue.blocked.absent');
  if (caught.status === 422) {
    const fields = caught.fieldErrors.map((error) => error.field).join(', ');
    return t('queue.refusal.fields', { fields: fields === '' ? t('queue.refusal.validation') : fields });
  }
  return caught.message;
}
